import React, { useState, useEffect } from 'react';
import { getGIPayouts, markGIPayoutPaid } from '../../model/api';
import { toast } from 'react-toastify';

export const GIPayoutsSubTab = ({ userRole }) => {
    const [payouts, setPayouts] = useState([]);
    const [groupedByMonthUser, setGroupedByMonthUser] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [statusFilter, setStatusFilter] = useState('unpaid'); // 'unpaid', 'paid', or 'all'

    // Payment Modal State
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedUserPayouts, setSelectedUserPayouts] = useState(null); // { user, payouts, totalAmount }
    const [transactionId, setTransactionId] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Bank Transfer');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [paymentNotes, setPaymentNotes] = useState('');
    const [processing, setProcessing] = useState(false);

    // Expanded states for details accordion
    const [expandedUsers, setExpandedUsers] = useState({});

    useEffect(() => {
        fetchPayouts();
    }, [selectedMonth, statusFilter]);

    const fetchPayouts = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            if (selectedMonth) params.month = selectedMonth;

            const result = await getGIPayouts(params);
            if (result.ok) {
                const rawPayouts = result.data.payouts || [];
                setPayouts(rawPayouts);
                processPayouts(rawPayouts);
            }
        } catch (error) {
            console.error('Error fetching GI payouts:', error);
            toast.error('Failed to fetch payouts');
        } finally {
            setLoading(false);
        }
    };

    const processPayouts = (rawPayouts) => {
        const grouped = {};

        rawPayouts.forEach(payout => {
            // 1. Group by Month (YYYY-MM)
            const dateObj = new Date(payout.createdAt); // Or payout_month if available
            const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;

            if (!grouped[monthKey]) grouped[monthKey] = {};

            // 2. Group by User ID
            const userId = payout.user_id?._id || payout.advisor_id || 'unknown';

            if (!grouped[monthKey][userId]) {
                grouped[monthKey][userId] = {
                    user: payout.user_id || { name: 'Unknown User', role: payout.user_role },
                    payouts: [],
                    totalAmount: 0,
                    isFullyPaid: true // Will turn false if any are unpaid
                };
            }

            grouped[monthKey][userId].payouts.push(payout);
            grouped[monthKey][userId].totalAmount += (payout.payout_amount || 0);
            if (payout.status !== 'paid') {
                grouped[monthKey][userId].isFullyPaid = false;
            }
        });

        setGroupedByMonthUser(grouped);
    };

    const toggleExpand = (key) => {
        setExpandedUsers(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const initiatePayment = (userGroup) => {
        // Filter only UNPAID payouts for this batch
        const unpaidPayouts = userGroup.payouts.filter(p => p.status !== 'paid');

        if (unpaidPayouts.length === 0) {
            toast.info('All payouts for this user are already paid.');
            return;
        }

        setSelectedUserPayouts({
            user: userGroup.user,
            payouts: unpaidPayouts,
            totalAmount: unpaidPayouts.reduce((sum, p) => sum + p.payout_amount, 0)
        });
        setTransactionId('');
        setPaymentDate(new Date().toISOString().split('T')[0]);
        setShowPaymentModal(true);
    };

    const handleMarkAsPaid = async () => {
        if (!selectedUserPayouts) return;
        if (!transactionId.trim()) {
            toast.warning('Please enter transaction ID/UTR');
            return;
        }

        setProcessing(true);
        let successCount = 0;
        let failCount = 0;

        try {
            // Process each payout one by one
            for (const payout of selectedUserPayouts.payouts) {
                try {
                    const result = await markGIPayoutPaid(payout._id, {
                        transaction_id: transactionId,
                        payment_date: paymentDate,
                        payment_method: paymentMethod,
                        payment_notes: paymentNotes
                    });

                    if (result.ok) successCount++;
                    else failCount++;

                } catch (err) {
                    console.error(`Failed to pay payout ${payout._id}`, err);
                    failCount++;
                }
            }

            if (successCount > 0) {
                toast.success(`Successfully marked ${successCount} payouts as Paid!`);
                setShowPaymentModal(false);
                setSelectedUserPayouts(null);
                fetchPayouts(); // Refresh data
            }

            if (failCount > 0) {
                toast.error(`Failed to update ${failCount} payouts.`);
            }

        } catch (error) {
            console.error('Bulk payment error:', error);
            toast.error('An error occurred during processing.');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return <div className="loading p-8 text-center text-gray-500">Loading GI payouts...</div>;
    }

    const months = Object.keys(groupedByMonthUser).sort().reverse();

    return (
        <div className="gi-payouts-tab">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <h3 className="text-lg font-semibold text-gray-800">GI Payouts Management</h3>

                <div className="flex flex-wrap gap-4 items-center">
                    {/* Status Filter */}
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                        {['unpaid', 'paid', 'all'].map(status => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors capitalize ${statusFilter === status
                                        ? 'bg-white text-indigo-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>

                    {/* Month Filter */}
                    <div className="flex items-center gap-2">
                        <input
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                        {selectedMonth && (
                            <button
                                onClick={() => setSelectedMonth('')}
                                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md text-sm"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {months.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-gray-500">No {statusFilter !== 'all' ? statusFilter : ''} payouts found.</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {months.map(monthKey => {
                        const monthData = groupedByMonthUser[monthKey];
                        const users = Object.values(monthData); // Array of user groups
                        const monthLabel = new Date(monthKey + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

                        return (
                            <div key={monthKey} className="space-y-4">
                                <h4 className="text-md font-bold text-gray-700 border-b pb-2 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                                    {monthLabel}
                                </h4>

                                <div className="grid gap-4">
                                    {users.map((group, idx) => {
                                        const uniqueKey = `${monthKey}-${group.user._id}`;
                                        const isExpanded = expandedUsers[uniqueKey];
                                        const hasUnpaid = group.payouts.some(p => p.status !== 'paid');

                                        return (
                                            <div key={uniqueKey} className={`border rounded-lg bg-white shadow-sm transition-all ${isExpanded ? 'ring-2 ring-indigo-50' : ''}`}>
                                                {/* Header / Summary Row */}
                                                <div className="p-4 flex flex-col md:flex-row justify-between items-center gap-4">

                                                    {/* User Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg">
                                                                {group.user.name?.charAt(0) || 'U'}
                                                            </div>
                                                            <div>
                                                                <h5 className="font-semibold text-gray-900 truncate">
                                                                    {group.user.name || 'Unknown User'}
                                                                    <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full capitalize">
                                                                        {group.user.role?.replace('_', ' ')}
                                                                    </span>
                                                                </h5>
                                                                <p className="text-sm text-gray-500">
                                                                    {group.payouts.length} Policy Commission{group.payouts.length !== 1 ? 's' : ''}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Amount & Actions */}
                                                    <div className="flex items-center gap-6">
                                                        <div className="text-right">
                                                            <p className="text-sm text-gray-500">Total Payout</p>
                                                            <p className="text-xl font-bold text-green-600">₹{group.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            {hasUnpaid ? (
                                                                <button
                                                                    onClick={() => initiatePayment(group)}
                                                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 shadow-sm"
                                                                >
                                                                    Pay Now
                                                                </button>
                                                            ) : (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                                    ✓ Paid
                                                                </span>
                                                            )}

                                                            <button
                                                                onClick={() => toggleExpand(uniqueKey)}
                                                                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-gray-100 rounded-full transition-colors"
                                                                title={isExpanded ? "Collapse Details" : "View Details"}
                                                            >
                                                                <svg className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Expanded Details */}
                                                {isExpanded && (
                                                    <div className="border-t bg-gray-50 p-4 rounded-b-lg animate-fadeIn">
                                                        <table className="w-full text-sm text-left">
                                                            <thead className="text-xs text-gray-500 uppercase bg-gray-100">
                                                                <tr>
                                                                    <th className="px-3 py-2">Policy / App ID</th>
                                                                    <th className="px-3 py-2">Customer</th>
                                                                    <th className="px-3 py-2">Percentage</th>
                                                                    <th className="px-3 py-2 text-right">Commission</th>
                                                                    <th className="px-3 py-2 text-center">Status</th>
                                                                    <th className="px-3 py-2">Payment Info</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200">
                                                                {group.payouts.map(p => (
                                                                    <tr key={p._id}>
                                                                        <td className="px-3 py-2 font-mono text-gray-600">{p.application_id?.application_id || 'N/A'}</td>
                                                                        <td className="px-3 py-2">
                                                                            {p.application_id?.customer_name}
                                                                            <span className="block text-xs text-gray-400">{p.application_id?.insurance_type}</span>
                                                                        </td>
                                                                        <td className="px-3 py-2">
                                                                            <div className="flex flex-col text-xs">
                                                                                <span>Comm: {p.commission_percentage}%</span>
                                                                                <span>Hier: {p.hierarchy_percentage}%</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-3 py-2 text-right font-medium">₹{p.payout_amount.toFixed(2)}</td>
                                                                        <td className="px-3 py-2 text-center">
                                                                            {p.status === 'paid' ?
                                                                                <span className="text-green-600 font-bold">✓</span> :
                                                                                <span className="text-yellow-500">Pending</span>
                                                                            }
                                                                        </td>
                                                                        <td className="px-3 py-2 text-xs text-gray-500">
                                                                            {p.status === 'paid' ? (
                                                                                <div>
                                                                                    <span className="font-mono text-gray-700">{p.transaction_id}</span>
                                                                                    <span className="block">{new Date(p.payment_date || p.paid_at).toLocaleDateString()}</span>
                                                                                </div>
                                                                            ) : '-'}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Bulk Payment Modal */}
            {showPaymentModal && selectedUserPayouts && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                    <div className="relative p-6 border w-96 shadow-xl rounded-xl bg-white animate-scaleIn">
                        <h3 className="text-xl font-bold text-gray-800 mb-1">Process Payment</h3>
                        <p className="text-sm text-gray-500 mb-6">Consolidated payment for {selectedUserPayouts.user.name}</p>

                        <div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-6">
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-sm text-green-800 font-medium">Total Payable Amount</span>
                                <span className="text-2xl font-bold text-green-700">₹{selectedUserPayouts.totalAmount.toLocaleString('en-IN')}</span>
                            </div>
                            <p className="text-xs text-green-600 text-right">for {selectedUserPayouts.payouts.length} entries</p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Payment Date</label>
                                <input
                                    type="date"
                                    value={paymentDate}
                                    onChange={(e) => setPaymentDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Transaction ID / UTR</label>
                                <input
                                    type="text"
                                    value={transactionId}
                                    onChange={(e) => setTransactionId(e.target.value)}
                                    placeholder="e.g. UPI1234567890"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all font-mono"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Method</label>
                                <select
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                                >
                                    <option value="Bank Transfer">Bank Transfer</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Notes </label>
                                <textarea
                                    value={paymentNotes}
                                    onChange={(e) => setPaymentNotes(e.target.value)}
                                    placeholder="Optional remarks..."
                                    rows="2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 outline-none resize-none"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-8">
                            <button
                                onClick={() => {
                                    setShowPaymentModal(false);
                                    setSelectedUserPayouts(null);
                                }}
                                disabled={processing}
                                className="flex-1 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleMarkAsPaid}
                                disabled={processing}
                                className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium shadow-md transition-all flex justify-center items-center"
                            >
                                {processing ? (
                                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                ) : (
                                    'Confirm Payment'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
